import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import inventoryApi from '../../../services/inventoryApi';
import '../inventory.css';
import './ProductForm.css';

/**
 * InventoryValuation Component
 * 
 * Shows inventory_valuations table data
 * Displays valuation snapshots for inventory at specific points in time
 * Useful for financial reporting and historical analysis
 */
const InventoryValuation = ({ organization = 'TestOrg' }) => {
  const { t } = useTranslation();
  const [valuations, setValuations] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    valuationDate: ''
  });

  useEffect(() => {
    loadProducts();
    loadValuations();
  }, [organization]);

  const loadProducts = async () => {
    try {
      const response = await inventoryApi.getProducts(organization);
      const productsData = Array.isArray(response) ? response : (response.data || []);
      setProducts(productsData);
    } catch (err) {
      console.error('Failed to load products:', err);
    }
  };

  const loadValuations = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const filterParams = {};
      if (filters.valuationDate) {
        filterParams.valuationDate = filters.valuationDate;
      }
      
      const response = await inventoryApi.getInventoryValuations(organization, filterParams);
      const valuationsData = Array.isArray(response) ? response : (response.data || []);
      setValuations(valuationsData);
    } catch (err) {
      setError(err.message || 'Failed to load inventory valuations');
      setValuations([]);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleApplyFilters = () => {
    loadValuations();
  };

  const handleClearFilters = () => {
    setFilters({
      valuationDate: ''
    });
    setTimeout(() => loadValuations(), 100);
  };

  const handleCreateSnapshot = async () => {
    if (!window.confirm(t('inventory.create_valuation_snapshot_button') + '?')) {
      return;
    }

    try {
      setError(null);
      await inventoryApi.createInventoryValuation({
        organization,
        valuation_date: new Date().toISOString().split('T')[0]
      });
      await loadValuations();
    } catch (err) {
      setError(err.message || 'Failed to create valuation snapshot');
    }
  };

  const getProductName = (productId) => {
    const product = products.find(p => p.id === productId);
    return product ? product.name : 'Unknown';
  };

  const getTotalValue = () => {
    return valuations.reduce((sum, v) => {
      const value = parseFloat(v.total_value || v.totalValue || 0);
      return sum + value;
    }, 0);
  };

  const getTotalQuantity = () => {
    return valuations.reduce((sum, v) => {
      const qty = parseFloat(v.quantity || 0);
      return sum + qty;
    }, 0);
  };

  const groupByDate = () => {
    const grouped = {};
    valuations.forEach(v => {
      const date = v.valuation_date || v.valuationDate || 'Unknown';
      if (!grouped[date]) {
        grouped[date] = [];
      }
      grouped[date].push(v);
    });
    return grouped;
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(value);
  };

  const groupedValuations = groupByDate();
  const valuationDates = Object.keys(groupedValuations).sort().reverse();

  return (
    <div className="pm-page-card">
      <div className="pm-header">
        <h2 className="pm-header-title">{t('inventory.inventory_valuation_title')}</h2>
        <button className="pm-btn-primary" onClick={handleCreateSnapshot}>
          <span aria-hidden="true">📸</span>
          <span>{t('inventory.create_valuation_snapshot_button')}</span>
        </button>
      </div>

      <p style={{ margin: '0 0 var(--pico-spacing)', color: 'var(--pico-muted-color)', fontSize: '0.95rem' }}>
        {t('inventory.valuation_description')}
      </p>

      {error && (
        <div className="pm-error-banner">
          <span aria-hidden="true">⚠️</span>
          <span>{error}</span>
        </div>
      )}

      <div className="pm-toolbar">
        <div className="pm-filter-wrap">
          <input
            type="date"
            name="valuationDate"
            value={filters.valuationDate}
            onChange={handleFilterChange}
          />
          <button className="pm-btn-primary" onClick={handleApplyFilters}>
            {t('inventory.apply_filters_button')}
          </button>
          <button className="pm-btn-secondary" onClick={handleClearFilters}>
            {t('inventory.clear_filters_button')}
          </button>
        </div>
      </div>

      <div className="pm-summary-grid">
        <div className="pm-summary-card">
          <h4>{t('inventory.total_records_card')}</h4>
          <p className="pm-summary-value">{valuations.length}</p>
        </div>
        <div className="pm-summary-card">
          <h4>{t('inventory.total_quantity_card')}</h4>
          <p className="pm-summary-value">{getTotalQuantity().toFixed(2)}</p>
        </div>
        <div className="pm-summary-card">
          <h4>{t('inventory.total_value_card')}</h4>
          <p className="pm-summary-value">{formatCurrency(getTotalValue())}</p>
        </div>
        <div className="pm-summary-card">
          <h4>{t('inventory.valuation_dates_card')}</h4>
          <p className="pm-summary-value">{valuationDates.length}</p>
        </div>
      </div>

      {loading ? (
        <div className="pm-skeleton" aria-busy="true">
          {t('inventory.loading_valuations')}
        </div>
      ) : valuations.length === 0 ? (
        <div className="pm-empty-state">
          <div className="pm-empty-state-icon" aria-hidden="true">💵</div>
          <h3>{t('inventory.no_valuations_found')}</h3>
          <p>{t('inventory.create_snapshot_prompt')}</p>
          <button className="pm-btn-primary" onClick={handleCreateSnapshot}>
            <span aria-hidden="true">📸</span>
            {t('inventory.create_valuation_snapshot_button')}
          </button>
        </div>
      ) : (
        <div>
          {valuationDates.map(date => {
            const dateValuations = groupedValuations[date];
            const dateTotal = dateValuations.reduce((sum, v) => 
              sum + parseFloat(v.total_value || v.totalValue || 0), 0
            );

            return (
              <div key={date} className="pm-valuation-group">
                <div className="pm-date-header">
                  <h3>{new Date(date).toLocaleDateString('en-US', { 
                    weekday: 'long', 
                    year: 'numeric', 
                    month: 'long', 
                    day: 'numeric' 
                  })}</h3>
                  <span className="pm-date-total">{t('inventory.date_total_label')} {formatCurrency(dateTotal)}</span>
                </div>

                <div className="pm-card-content">
                  <table className="pm-data-table">
                    <thead>
                      <tr>
                        <th>{t('inventory.product_header')}</th>
                        <th>{t('inventory.quantity_header')}</th>
                        <th>{t('inventory.unit_cost_header')}</th>
                        <th>{t('inventory.total_value_header')}</th>
                        <th>{t('inventory.valuation_method_header')}</th>
                        <th>{t('inventory.notes_header')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {dateValuations.map((valuation, idx) => (
                        <tr key={idx}>
                          <td>{getProductName(valuation.product_id || valuation.productId)}</td>
                          <td>{parseFloat(valuation.quantity || 0).toFixed(2)}</td>
                          <td>{formatCurrency(valuation.unit_cost || valuation.unitCost || 0)}</td>
                          <td className="pm-value-cell">
                            {formatCurrency(valuation.total_value || valuation.totalValue || 0)}
                          </td>
                          <td>
                            <span className="pm-method-badge">
                              {(valuation.valuation_method || valuation.valuationMethod || 'FIFO').toUpperCase()}
                            </span>
                          </td>
                          <td>{valuation.notes || t('inventory.no_notes')}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default InventoryValuation;
